import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { SocialRail, SOCIAL_LINKS, SOCIAL_PLATFORMS, SocialMark } from '../components/SocialIcons';
import { api } from '../api';
import { HOME_CONTENT, mergePageContent } from '../content/pageDefaults';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&h=600&fit=crop';

const SectionEyebrow = ({ children, light = false }) => (
  <div className={`flex items-center gap-3 mb-4 ${light ? '' : ''}`}>
    <span className="ornament"><i /><span /><i /></span>
    <p className={`text-[11px] tracking-[0.28em] uppercase font-semibold ${light ? 'text-accent' : 'text-primary'}`}>
      {children}
    </p>
  </div>
);

const BLOG_PLACEHOLDER = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&h=500&fit=crop';

const CATEGORY_COLORS = {
  'umrah-tips':    { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  'packing-guide': { bg: 'bg-amber-100',   text: 'text-amber-800'   },
  'visa-updates':  { bg: 'bg-blue-100',    text: 'text-blue-800'    },
  'dua-guide':     { bg: 'bg-purple-100',  text: 'text-purple-800'  },
  'news':          { bg: 'bg-red-100',     text: 'text-red-800'     },
  'travel-guide':  { bg: 'bg-teal-100',    text: 'text-teal-800'    },
  'hajj-tips':     { bg: 'bg-orange-100',  text: 'text-orange-800'  },
};

const LandingPage = ({ navigate }) => {
  const [categories, setCategories] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [page, setPage] = useState(HOME_CONTENT);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (window.matchMedia('(hover: none)').matches) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 18;
      setParallax({ x, y });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const loadData = async () => {
    setLoading(true);
    setBackendError(false);
    try {
      const [categoriesData, packagesData, itemsData, blogData, pageData] = await Promise.all([
        api.getCategories(),
        api.getFeaturedPackages(),
        api.getItems(),
        api.getLatestBlogPosts(3).catch(() => []),
        api.getPageContent('home').catch(() => null),
      ]);
      if (pageData?.content) setPage(mergePageContent(HOME_CONTENT, pageData.content));
      const cats = Array.isArray(categoriesData) ? categoriesData : (categoriesData.results || []);
      const packages = Array.isArray(packagesData) ? packagesData : (packagesData.results || []);
      const shopItems = Array.isArray(itemsData) ? itemsData : (itemsData.results || []);
      setCategories(cats.filter((c) => c.category_type !== 'item'));
      setFeaturedPackages(packages);
      setItems(shopItems.slice(0, 4));
      setBlogPosts(Array.isArray(blogData) ? blogData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      setBackendError(true);
    } finally {
      setLoading(false);
    }
  };

  const faqs = page.faq?.items || [];
  const marqueeItems = page.marquee || [];

  return (
    <div className="bg-cream overflow-x-hidden">
      <Navbar navigate={navigate} transparent />

      {backendError && (
        <div className="fixed top-20 left-0 right-0 z-40 bg-primary text-cream text-center py-3 px-4 text-sm font-medium shadow">
          Unable to connect to server. Some content may not load.
          <button onClick={loadData} className="ml-3 underline font-bold">Retry</button>
        </div>
      )}

      <section className="relative min-h-[100svh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={page.hero.image}
            alt="Kaaba"
            className="w-full h-full object-cover hero-kenburns"
            onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
          />
          <div className="absolute inset-0 bg-ink/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/50 to-ink/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-ink/30" />
          <div className="hero-light" />
          <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #C9A45C 0, transparent 32%), radial-gradient(circle at 80% 80%, #C9A45C 0, transparent 28%)' }} />
        </div>

        <div
          className="absolute top-28 right-10 md:right-24 w-24 h-24 border border-accent/30 rotate-45 hidden md:block"
          style={{ transform: `translate(${parallax.x}px, ${parallax.y}px) rotate(45deg)` }}
        />
        <div
          className="absolute bottom-32 right-40 w-10 h-10 border border-accent/40 rotate-45 hidden lg:block"
          style={{ transform: `translate(${parallax.x * -0.6}px, ${parallax.y * -0.6}px) rotate(45deg)` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 sm:py-16 md:py-14 sm:py-20 lg:py-24 w-full">
          <div className="max-w-3xl hero-stagger">
            <div>
              <span className="inline-flex items-center gap-3 text-accent text-[10px] sm:text-[11px] font-semibold tracking-[0.22em] sm:tracking-[0.28em] uppercase">
                <span className="w-8 h-px bg-accent hidden sm:inline-block" />
                {page.hero.eyebrow}
              </span>
            </div>
            <h1 className="font-display text-[2.15rem] leading-[1.08] sm:text-5xl md:text-7xl lg:text-[5.4rem] font-semibold text-white sm:leading-[1.05] mt-4 sm:mt-5">
              {page.hero.title}
              <span className="block italic text-accent">{page.hero.titleAccent}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed max-w-xl mt-2">
              {page.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <button
                onClick={() => navigate('category', { category: 'umrah-packages' })}
                className="btn-lux gradient-gold text-ink px-8 py-3.5 sm:py-4 rounded-sm font-semibold text-base tracking-wide min-h-11">
                {page.hero.primaryButton}
              </button>
              <button
                onClick={() => navigate('contact')}
                className="text-white border border-white/30 hover:border-accent hover:text-accent px-8 py-3.5 sm:py-4 rounded-sm font-semibold text-base tracking-wide transition-colors min-h-11">
                {page.hero.secondaryButton}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-6 pt-8 sm:pt-10 mt-4 border-t border-white/30 max-w-lg">
              {(page.stats || []).map((stat, i) => {
                const n = stat.value;
                const l = stat.label;
                return (
                <div key={`${l}-${i}`} className="stat-pop" style={{ animationDelay: `${1 + i * 0.12}s` }}>
                  <div className="font-display text-xl sm:text-3xl text-white">{n}</div>
                  <div className="text-white/70 text-[10px] sm:text-xs mt-1 tracking-wide leading-snug">{l}</div>
                </div>
              );})}
            </div>
            <div className="hero-social-panel">
              <p className="text-[11px] tracking-[0.22em] uppercase text-accent font-semibold">Message or follow</p>
              <SocialRail tone="light" links={SOCIAL_PLATFORMS} />
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 scroll-hint text-accent text-[10px] tracking-[0.3em] uppercase hidden sm:block">
          Scroll
        </div>
      </section>

      {marqueeItems.length > 0 && (
      <section className="bg-ink text-accent py-4 border-y border-accent/20">
        <div className="marquee">
          <div className="marquee-track text-xs tracking-[0.22em] uppercase">
            {[...marqueeItems, ...marqueeItems].map((item, i) => (
              <span key={i} className="flex items-center gap-4">
                <span className="w-1.5 h-1.5 rotate-45 bg-accent inline-block" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="py-14 sm:py-20 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6 relative overflow-visible px-0 sm:px-2 mb-10 lg:mb-0">
              <div className="gold-frame relative">
                <img
                  src={page.about.image}
                  alt="Umrah Journey"
                  className="w-full h-[240px] sm:h-[380px] lg:h-[520px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 right-2 sm:-bottom-5 sm:right-2 md:-right-6 bg-ink text-accent px-4 py-4 sm:px-7 sm:py-6 shadow-2xl float-slow">
                <p className="font-display text-3xl sm:text-4xl leading-none">{page.about.badgeValue}</p>
                <p className="text-[11px] tracking-[0.18em] uppercase mt-2 text-sand">{page.about.badgeLabel}</p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-6 lg:pl-8" delay={120}>
              <SectionEyebrow>{page.about.eyebrow}</SectionEyebrow>
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink mb-6 leading-tight">
                {page.about.title}
              </h2>
              {(page.about.paragraphs || []).map((paragraph, i, list) => (
                <p key={i} className={`text-ink/70 leading-relaxed ${i === list.length - 1 ? 'mb-8' : 'mb-5'}`}>
                  {paragraph}
                </p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                {(page.about.highlights || []).map((t) => (
                  <div key={t} className="border-t border-accent pt-3">
                    <p className="text-sm font-medium text-ink">{t}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('about')}
                className="text-primary font-semibold tracking-wide inline-flex items-center gap-2 group">
                {page.about.cta}
                <span className="w-8 h-px bg-accent group-hover:w-14 transition-all" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal className="mb-14">
            <SectionEyebrow light>{page.journeys.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl">{page.journeys.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              [...Array(4)].map((_, i) => <div key={i} className="h-[260px] sm:h-[360px] lg:h-[420px] bg-secondary animate-pulse" />)
            ) : categories.length === 0 ? (
              <p className="col-span-full text-sand/70">Categories will be available soon</p>
            ) : categories.map((category, i) => (
              <Reveal key={category.id} delay={i * 90}>
                <button
                  onClick={() => navigate('category', { category: category.category_type })}
                  className="lux-card group relative block w-full text-left overflow-hidden h-[260px] sm:h-[360px] lg:h-[420px] bg-secondary border-0">
                  <img
                    src={category.image || PLACEHOLDER_IMG}
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90"
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="text-accent text-[10px] tracking-[0.25em] uppercase mb-2">0{i + 1}</p>
                    <h3 className="text-2xl text-white mb-2">{category.name}</h3>
                    <p className="text-sand/80 text-sm line-clamp-2 mb-4">{category.description}</p>
                    <span className="text-accent text-sm tracking-wide inline-flex items-center gap-2">
                      Discover <span className="w-6 h-px bg-accent group-hover:w-10 transition-all" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <SectionEyebrow>{page.featured.eyebrow}</SectionEyebrow>
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.featured.title}</h2>
            </Reveal>
            <button
              onClick={() => navigate('category', { category: 'umrah-packages' })}
              className="text-primary font-semibold inline-flex items-center gap-2 group">
              {page.featured.cta}
              <span className="w-8 h-px bg-accent group-hover:w-14 transition-all" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {loading ? (
              [...Array(3)].map((_, i) => <div key={i} className="h-96 bg-sand animate-pulse" />)
            ) : featuredPackages.length === 0 ? (
              <p className="col-span-full text-ink/50">Featured packages will be available soon</p>
            ) : featuredPackages.slice(0, 6).map((pkg, i) => (
              <Reveal key={pkg.id} delay={i * 80}>
                <div
                  onClick={() => navigate('package', { package: pkg })}
                  className="lux-card cursor-pointer overflow-hidden">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={pkg.featured_image || PLACEHOLDER_IMG}
                      alt={pkg.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                    {pkg.is_featured && (
                      <span className="absolute top-4 left-4 gradient-gold text-ink px-3 py-1 text-[10px] font-semibold tracking-[0.16em] uppercase">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-primary/70 mb-2">
                      {pkg.duration_days} days · {pkg.duration_nights} nights
                    </p>
                    <h3 className="text-2xl text-ink mb-2">{pkg.name}</h3>
                    <p className="text-ink/60 text-sm line-clamp-2 mb-5">{pkg.short_description}</p>
                    <div className="flex items-end justify-between pt-4 border-t border-sand">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-ink/40">From</p>
                        <p className="font-display text-3xl text-primary">${pkg.min_price}</p>
                      </div>
                      <span className="btn-lux gradient-gold text-ink px-5 py-2 text-sm font-semibold">Reserve</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {items.length > 0 && (
        <section className="py-14 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal className="mb-12">
              <SectionEyebrow>{page.shop.eyebrow}</SectionEyebrow>
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.shop.title}</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {items.map((item, i) => (
                <Reveal key={item.id} delay={i * 70}>
                  <button onClick={() => navigate('shop')} className="lux-card w-full text-left overflow-hidden">
                    <div className="aspect-square overflow-hidden bg-sand">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl text-primary/30">+</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg text-ink">{item.name}</h3>
                      <p className="text-primary font-semibold mt-1">${item.price}</p>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 sm:py-20 lg:py-24 bg-ink text-white relative overflow-hidden">
        <div className="absolute -right-20 top-10 w-72 h-72 border border-accent/10 rotate-45" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <Reveal className="text-center mb-16">
            <SectionEyebrow light>{page.why.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl">{page.why.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-accent/20">
            {(page.why.items || []).map((f, i) => (
              <Reveal key={f.title} delay={i * 60} className="bg-ink p-8 hover:bg-secondary transition-colors duration-500">
                <p className="text-accent font-display text-3xl mb-4">0{i + 1}</p>
                <h3 className="text-xl mb-2">{f.title}</h3>
                <p className="text-sand/70 text-sm leading-relaxed">{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal className="text-center mb-16">
            <SectionEyebrow>{page.testimonials.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.testimonials.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(page.testimonials.items || []).map((t, i) => (
              <Reveal key={t.name} delay={i * 100} className="lux-card p-8">
                <p className="font-display text-6xl text-accent/40 leading-none mb-3">“</p>
                <p className="text-ink/75 leading-relaxed mb-6">{t.review}</p>
                <div className="border-t border-sand pt-4">
                  <p className="font-semibold text-ink">{t.name}</p>
                  <p className="text-xs tracking-[0.16em] uppercase text-primary mt-1">{t.pkg}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <Reveal className="text-center mb-16">
            <SectionEyebrow>{page.process.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.process.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 relative">
            <div className="hidden md:block absolute top-8 left-[12%] right-[12%] h-px bg-sand" />
            {(page.process.steps || []).map((item, i) => (
              <Reveal key={item.step} delay={i * 90} className="text-center px-4 relative">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-cream border border-accent text-primary font-display text-xl flex items-center justify-center relative z-10">
                  {item.step}
                </div>
                <h3 className="text-xl text-ink mb-2">{item.title}</h3>
                <p className="text-ink/60 text-sm">{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-cream">
        <div className="max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-12">
            <SectionEyebrow>{page.faq.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.faq.title}</h2>
          </Reveal>
          <div className="divide-y divide-sand border-y border-sand">
            {faqs.map((faq, i) => (
              <Reveal key={faq.q} delay={i * 50}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left py-5 min-h-11 flex justify-between gap-4 sm:gap-6 group"
                >
                  <span className="text-lg text-ink pr-4">{faq.q}</span>
                  <span className={`text-accent text-2xl leading-none transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? 'max-h-40 pb-5' : 'max-h-0'}`}>
                  <p className="text-ink/65 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <SectionEyebrow>{page.contact.eyebrow}</SectionEyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.contact.title}</h2>
            <p className="text-ink/60 mt-3 max-w-xl mx-auto">{page.contact.subtitle}</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOCIAL_LINKS.map((item, i) => (
              <Reveal key={item.id} delay={i * 70}>
                <a
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="contact-tile lux-card flex items-center gap-4 p-5 no-underline"
                  style={{ '--social': item.color }}
                >
                  <SocialMark id={item.id} color={item.color} />
                  <span>
                    <span className="block text-ink font-semibold">{item.label}</span>
                    <span className="block text-sm text-ink/55 mt-0.5">{item.hint}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Blog / News Section ──────────────────────────────────── */}
      {blogPosts.length > 0 && (
        <section className="py-14 sm:py-20 lg:py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <Reveal>
                <p className="flex items-center gap-3 mb-4">
                  <span className="ornament"><i /><span /><i /></span>
                  <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-primary">{page.blog.eyebrow}</span>
                </p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl text-ink">{page.blog.title}</h2>
              </Reveal>
              <button
                onClick={() => navigate('blog')}
                className="text-primary font-semibold inline-flex items-center gap-2 group shrink-0">
                {page.blog.cta}
                <span className="w-8 h-px bg-accent group-hover:w-14 transition-all" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogPosts.map((post, i) => {
                const catColor = CATEGORY_COLORS[post.category] || { bg: 'bg-sand', text: 'text-ink' };
                return (
                  <Reveal key={post.id} delay={i * 80}>
                    <div
                      onClick={() => navigate('blog-detail', { slug: post.slug })}
                      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                      <div className="aspect-[16/10] overflow-hidden bg-sand">
                        <img
                          src={post.featured_image || BLOG_PLACEHOLDER}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => { e.target.onerror = null; e.target.src = BLOG_PLACEHOLDER; }}
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${catColor.bg} ${catColor.text}`}>
                            {post.category_display}
                          </span>
                          <span className="text-xs text-ink/40">{post.read_time_minutes} min read</span>
                        </div>
                        <h3 className="text-lg font-semibold text-ink leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors flex-1">
                          {post.title}
                        </h3>
                        <p className="text-sm text-ink/50 line-clamp-2 mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between pt-3 border-t border-sand">
                          <p className="text-xs text-ink/40">
                            {post.published_at
                              ? new Date(post.published_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
                              : ''}
                          </p>
                          <span className="text-primary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read <span className="group-hover:translate-x-1 transition-transform">→</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="relative py-16 sm:py-24 lg:py-28 overflow-hidden">
        <img
          src={page.cta.image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/80" />
        <Reveal className="relative max-w-3xl mx-auto px-4 text-center text-white">
          <p className="text-accent tracking-[0.28em] uppercase text-[11px] mb-4">{page.cta.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl mb-6">{page.cta.title}</h2>
          <p className="text-sand/80 mb-10">{page.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('category', { category: 'umrah-packages' })}
              className="btn-lux gradient-gold text-ink px-8 py-4 font-semibold">
              {page.cta.primaryButton}
            </button>
            <button
              onClick={() => navigate('contact')}
              className="border border-accent/60 text-white hover:bg-white/5 px-8 py-4 font-semibold">
              {page.cta.secondaryButton}
            </button>
          </div>
          <div className="flex justify-center mt-10">
            <SocialRail tone="light" links={SOCIAL_PLATFORMS} />
          </div>
        </Reveal>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
};

export default LandingPage;
